import React, { useState, useRef } from 'react';
import { CheckCircle, TrendingUp, DollarSign, Users, Zap, ArrowRight, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { franchiseModels } from '../data/mock';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import ROICalculator from '../components/ROICalculator';

const Franchise = () => {
  const [selectedModel, setSelectedModel] = useState(franchiseModels[1]);
  const roiSectionRef = useRef(null);

  // Handle "Learn More" click - scroll to ROI and set model
  const handleLearnMore = (model, e) => {
    e.stopPropagation();
    setSelectedModel(model);
    
    // Scroll to ROI section
    roiSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6">
            <span className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium">
              Franchise Opportunities
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Partner with <span className="text-cyan-400">HVGTEC</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Proven franchise models from Micro POPs to Regional Cores with clear ROI projections
          </p>
        </div>
      </section>

      {/* Franchise Models */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Choose Your Model
            </h2>
            <p className="text-xl text-gray-400 mb-6">
              Scalable solutions for every market size
            </p>
            
            {/* Download Prospectus Button */}
            <div className="inline-flex flex-col sm:flex-row items-center gap-4 mt-6">
              <a 
                href="https://customer-assets.emergentagent.com/job_hvgtec-home/artifacts/qeemnt1q_ISP%20Franchise%20Prospectus_%20Case%20Study.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-cyan-500 hover:bg-cyan-600 text-white rounded-xl transition-all duration-200 shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 hover:scale-105"
              >
                <Download className="mr-2" size={20} />
                Download Franchise Prospectus
              </a>
              <p className="text-sm text-gray-400">
                Learn about our ₱241,000 and ₱402,000 Micro POP packages
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {franchiseModels.map((model) => (
              <div 
                key={model.id}
                className={`bg-slate-900 border rounded-2xl p-8 transition-all duration-300 hover:scale-105 cursor-pointer ${
                  model.id === 2
                    ? 'border-cyan-500 shadow-lg shadow-cyan-500/20 relative'
                    : 'border-slate-700 hover:border-cyan-500/50'
                }`}
                onClick={() => setSelectedModel(model)}
              >
                {model.id === 2 && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-cyan-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Recommended
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-3xl font-bold text-white mb-2">{model.name}</h3>
                  <div className="text-cyan-400 text-xl font-semibold mb-4">{model.capacity}</div>
                  <p className="text-gray-400 text-sm mb-4">{model.description}</p>
                  <div className="text-4xl font-bold text-white mb-2">{model.investment}</div>
                  <div className="text-sm text-gray-400">Initial Investment</div>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-sm text-gray-400 mb-1">ROI Period</div>
                    <div className="text-xl font-bold text-cyan-400">{model.roi}</div>
                    {model.roiNote && (
                      <div className="text-xs text-gray-500 mt-1 italic">*{model.roiNote}</div>
                    )}
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-4">
                    <div className="text-sm text-gray-400 mb-1">Expected Revenue</div>
                    <div className="text-xl font-bold text-cyan-400">{model.revenue}</div>
                    {model.revenueNote && (
                      <div className="text-xs text-gray-500 mt-1 italic">*{model.revenueNote}</div>
                    )}
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-white font-semibold mb-3">Key Features</h4>
                  <ul className="space-y-2">
                    {model.features.slice(0, 4).map((feature, index) => (
                      <li key={index} className="flex items-start text-gray-300 text-sm">
                        <CheckCircle className="text-cyan-400 mr-2 mt-0.5 flex-shrink-0" size={16} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Button 
                  onClick={(e) => handleLearnMore(model, e)}
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-white py-3 rounded-xl transition-all duration-200"
                  data-testid={`learn-more-${model.id}`}
                >
                  Learn More
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section ref={roiSectionRef} className="py-24 bg-slate-900" id="roi-calculator">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              ROI Calculator
            </h2>
            <p className="text-xl text-gray-400 mb-2">
              Detailed investment analysis for {selectedModel?.name || 'your franchise'}
            </p>
            <p className="text-sm text-gray-500">
              Adjust the values below to match your specific situation. All calculations update in real-time.
            </p>
          </div>

          <ROICalculator initialModel={selectedModel} key={selectedModel?.id} />
          
          <p className="text-xs text-gray-500 text-center mt-8">
            * These projections are estimates based on typical market conditions. Actual results may vary depending on location, 
            market penetration, competition, and operational efficiency. Consult with HVGTEC for detailed feasibility studies.
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-24 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              What's Included
            </h2>
            <p className="text-xl text-gray-400">
              Complete support from deployment to operation
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Equipment Package</h3>
              <ul className="space-y-2 text-gray-400">
                <li>• Complete hardware (routers, switches, antennas)</li>
                <li>• Solar panels & battery systems (for Micro POP)</li>
                <li>• Professional installation</li>
                <li>• Initial configuration & testing</li>
              </ul>
            </div>

            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Training & Support</h3>
              <ul className="space-y-2 text-gray-400">
                <li>• Technical training for your team</li>
                <li>• Business operations guide</li>
                <li>• 24/7 technical support hotline</li>
                <li>• Remote monitoring & management</li>
              </ul>
            </div>

            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-white mb-4">Documentation</h3>
              <ul className="space-y-2 text-gray-400">
                <li>• Network architecture diagrams</li>
                <li>• Operations manual</li>
                <li>• Troubleshooting guides</li>
                <li>• Access to technical portal</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-cyan-950/30 to-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Start Your ISP Business?
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Contact us today for a detailed franchise package and personalized consultation.
          </p>
          <Link to="/contact">
            <Button className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-cyan-500/20 transition-all duration-200">
              Request Franchise Information
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Franchise;
